import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal — scroll-triggered entrance wrapper.
 *
 * Robustness: IntersectionObserver is the primary trigger, with a
 * rAF-throttled scroll/resize fallback (getBoundingClientRect check) so
 * content can never stay hidden if IO is unavailable or suppressed.
 * Reduced-motion users see content immediately (CSS handles it).
 *
 * Props:
 *  - as: wrapper tag (default 'div')
 *  - variant: '' | 'left' | 'right' | 'scale'
 *  - delay: ms stagger delay
 */
const Reveal = ({ as: Tag = 'div', variant = '', delay = 0, className = '', children, ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let revealed = false;
    let ticking = false;
    let observer;

    const reveal = () => {
      if (revealed) return;
      revealed = true;
      setVisible(true);
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', onScrollCheck);
      window.removeEventListener('resize', onScrollCheck);
    };

    const inView = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return rect.top < vh - 40 && rect.bottom > 0;
    };

    const onScrollCheck = () => {
      if (ticking || revealed) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (inView()) reveal();
      });
    };

    // Immediate check: already on screen at mount (above the fold,
    // deep links, or environments where IO never fires).
    if (inView()) {
      reveal();
      return undefined;
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) reveal();
        },
        { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
      );
      observer.observe(el);
    }

    // Fallback path — cheap, self-removing once revealed
    window.addEventListener('scroll', onScrollCheck, { passive: true });
    window.addEventListener('resize', onScrollCheck, { passive: true });

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', onScrollCheck);
      window.removeEventListener('resize', onScrollCheck);
    };
  }, []);

  const variantClass = variant ? ` reveal--${variant}` : '';
  return (
    <Tag
      ref={ref}
      className={`reveal${variantClass}${visible ? ' is-visible' : ''} ${className}`}
      style={delay ? { '--reveal-delay': `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
